import React, { useState } from 'react';
import AddIcon from '../../../assets/icons/add-icon.svg';
import styles from './AddMilestoneFooter.module.scss';
import { updateMoment } from '../../../components/RsdForms/api';

interface AddMilestoneFooterProps {
  onDataRefresh?: (patchResponse?: unknown) => void;
  issueId: string; // Required for API calls
  milestoneId?: string; // Optional, not always needed
  phaseId?: string; // For phased releases
  phaseName?: string; // For phased releases
  momentName?: string;
  momentType?: string;
  // Optional client-side add handler (e.g., duplicate UI). If provided, skips API.
  onAdd?: () => void;
  disabled?: boolean;
}

const AddMilestoneFooter: React.FC<AddMilestoneFooterProps> = ({
  onDataRefresh,
  issueId,
  milestoneId,
  phaseId,
  phaseName,
  momentName,
  momentType,
  onAdd,
  disabled
}) => {
  // Add milestone state
  const [isAddingMilestone, setIsAddingMilestone] = useState(false);

  // Add new milestone handler
  const handleAddMilestone = async () => {
    if (isAddingMilestone || disabled) return; // Prevent multiple clicks

    setIsAddingMilestone(true);

    try {
      // If a client-side handler is provided, use it and skip API
      if (onAdd) {
        onAdd();
        return;
      }

      // Validate required issueId
      if (!issueId) {
        throw new Error('Issue ID is required for milestone operations');
      }

      // Create payload for setting milestoneAdded flag to true
      let payload: any;

      if (phaseId) {
        // Phased release: Update specific phase
        payload = {
          releasePhases: [
            {
              id: phaseId,
              // ...(phaseName && { phaseName }),
              milestoneAdded: true
            }
          ]
        };
      } else {
        // Non-phased release: Update root level
        payload = {
          milestoneAdded: true
        };
      }

      const result = await updateMoment(issueId, payload);

      // Seamless refresh - no loaders, just refresh the data
      // This will trigger the SlideOutPanel to re-fetch milestone data
      if (onDataRefresh) {
        await onDataRefresh(result);
      }
    } catch (error) {
      // You might want to show a user-friendly error message here
      alert('Failed to add milestone. Please try again.');
    } finally {
      setIsAddingMilestone(false);
    }
  };

  return (
    <div className={styles.footerActions} data-testid="milestones-footer">
      <button
        type="button"
        className={styles.addButton}
        onClick={handleAddMilestone}
        disabled={disabled || isAddingMilestone}
        data-testid="milestones-add-button"
      >
        <span data-testid="milestones-add-label">
          {isAddingMilestone ? 'Adding...' : 'Add Milestones'}
        </span>
        <img
          src={AddIcon}
          alt="Add"
          width={8}
          height={8}
          className={styles.addIcon}
          data-testid="milestones-add-icon"
        />
      </button>
    </div>
  );
};

export default AddMilestoneFooter;
